import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxMaskPipe } from 'ngx-mask';

import { Types } from '../../../domain/types.domain';
import {
  GovBrCompany,
  StartAccountCreationOutput,
} from '../../../domain/auth/dtos/start-account-creation.output';
import { AuthRepositoryInstruction } from '../../../builder/instructions/repositories/auth.repository.instructions';

import { AppHandler } from '../../app.handler';
import { RedirectService } from '../../redirect.service';
import { PageTitleComponent } from '../../components/abstracts/page-title.component';
import { RegistrationCompletionState } from '../registration-completion/registration-completion.component';
import { IconComponent } from '../../components/icon/icon.component';
import { CreateAccountPjState } from '../create-account-pj/create-account-pj.component';
import {
  OidcContinuation,
  govBrAuthorizeBaseUrl,
  govBrLoginUrl,
  postAuthContinuation,
} from '../../utils/govbr-authorize';
import { accountCreationError } from '../../utils/account-creation-error';

export interface AccountOptionsState {
  accountOptions: StartAccountCreationOutput;
  customUrlRedirect?: string;
  continuation?: OidcContinuation;
}

@Component({
  selector: 'app-account-options',
  standalone: true,
  imports: [NgxMaskPipe, IconComponent],
  providers: [new AuthRepositoryInstruction()],
  templateUrl: './account-options.component.html',
  styleUrls: ['./account-options.component.scss'],
})
export class AccountOptionsComponent
  extends PageTitleComponent('Criar conta')
  implements OnInit
{
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _appHandler = inject(AppHandler);
  private readonly _redirectService = inject(RedirectService);
  private readonly _authRepository = inject(Types.AuthRepository);

  private readonly _state = this._router.getCurrentNavigation()?.extras?.state as
    | AccountOptionsState
    | undefined;

  public readonly options = this._state?.accountOptions;
  /** Sem conta pessoal nem empresa do gov.br a oferecer, o cadastro pelo CNPJ não é "outra" opção. */
  public readonly companyByCnpjIsTheOnlyOption =
    !this.options?.personalAccountAvailable && !this.options?.companies.length;
  public creating = signal(false);

  private get city() {
    return this._route.snapshot.data['city'];
  }

  ngOnInit(): void {
    this._appHandler.setLoading(false);
    this._appHandler.setBackButtonHome(false);

    if (!this.options) {
      this.backToLogin();
    }
  }

  createPersonalAccount(): void {
    if (this.creating()) return;
    this.creating.set(true);

    this._authRepository
      .createPersonalAccount({
        contextId: this.options!.contextId,
        connection: this.city.connection,
        ...(this._state?.customUrlRedirect
          ? { customUrlRedirect: this._state.customUrlRedirect }
          : {}),
      })
      .then(({ code, registrationToken, userData, redirectUri }) => {
        if (registrationToken) {
          const state: RegistrationCompletionState = {
            registrationToken,
            account: userData!,
            continuation: this._state?.continuation,
          };
          this._router.navigate([`/${this.city.identidade}/registration-completion`], { state });
          return;
        }

        this._redirectService.postAuth({
          redirectUri,
          code,
          ...postAuthContinuation(this._state?.continuation ?? {}),
        });
      })
      .catch((errorResponse: any) => {
        this.creating.set(false);
        this._appHandler.setError(true, {
          ...accountCreationError(errorResponse?.error),
          actionText: 'Voltar',
          action: () => {
            this._appHandler.clearError();
            this.backToLogin();
          },
        });
      });
  }

  registerCompany(company: GovBrCompany): void {
    this._router.navigate([`/${this.city.identidade}/company-registration`], {
      state: { company, contextId: this.options!.contextId, origin: this._state },
    });
  }

  async loginWithGovBr(): Promise<void> {
    try {
      const config = await this._authRepository.getConfigByCityId(this.city.index);
      const authorizeUrl = govBrAuthorizeBaseUrl(config);
      if (!authorizeUrl) throw new Error('Cidade sem login gov.br');
      this._redirectService.toProvider(
        govBrLoginUrl(authorizeUrl, this._state?.customUrlRedirect ?? '', this._state?.continuation)
      );
    } catch {
      this._appHandler.setError(true, {
        message: 'Não foi possível abrir o gov.br.',
        description: 'Tente de novo. Se o problema persistir, entre em contato com nossa equipe de suporte.',
        actionText: 'Voltar',
        action: () => this._appHandler.clearError(),
      });
    }
  }

  registerOtherCompany(): void {
    const state: CreateAccountPjState = { verifiedRepresentative: this.options!.representative };
    this._router.navigate([`/${this.city.identidade}/create-account-pj`], { state });
  }

  backToLogin(): void {
    if (this.city.loginUrl) {
      window.location.href = this.city.loginUrl;
      return;
    }

    this._router.navigate([`/${this.city.identidade}`]);
  }
}
